"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "../AnimatedSection";
import { Tablet, Smartphone, Sparkles, CheckCircle2 } from "lucide-react";
import type { LucideIcon } from "lucide-react";

interface Device {
  icon: LucideIcon;
  pillEn: string;
  pillEs: string;
  pillColor: string;
  pillBg: string;
  comingSoon?: boolean;
  titleEn: string;
  titleEs: string;
  bodyEn: string;
  bodyEs: string;
  bulletsEn: string[];
  bulletsEs: string[];
}

const DEVICES: Device[] = [
  {
    icon: Tablet,
    pillEn: "Available now",
    pillEs: "Disponible ya",
    pillColor: "text-redy-green",
    pillBg: "bg-redy-green/10",
    titleEn: "Any iPad",
    titleEs: "Cualquier iPad",
    bodyEn:
      "Bring an iPad you already own (any model from iPad 7 onward). Download Redy from the App Store — no jailbreak, no MDM, no IT.",
    bodyEs:
      "Trae el iPad que ya tienes (iPad 7 o más nuevo). Descarga Redy del App Store — sin jailbreak, sin MDM, sin IT.",
    bulletsEn: [
      "iPad, iPad Air, iPad Pro — all supported",
      "Pairs with North terminal over Bluetooth",
      "Runs POS, KDS, kiosk and customer display",
    ],
    bulletsEs: [
      "iPad, iPad Air, iPad Pro — todos compatibles",
      "Empareja con terminal North vía Bluetooth",
      "Corre POS, KDS, kiosko y customer display",
    ],
  },
  {
    icon: Tablet,
    pillEn: "Available now",
    pillEs: "Disponible ya",
    pillColor: "text-redy-green",
    pillBg: "bg-redy-green/10",
    titleEn: "Any Android Tablet",
    titleEs: "Cualquier Tablet Android",
    bodyEn:
      "Already on Samsung, Lenovo or another Android tablet? Redy runs natively — no Apple ecosystem required, no Chromebook workarounds.",
    bodyEs:
      "¿Ya tienes Samsung, Lenovo u otra tablet Android? Redy corre nativo — sin necesidad de ecosistema Apple, sin workarounds de Chromebook.",
    bulletsEn: [
      "Samsung Galaxy Tab, Lenovo Tab, any Android 10+",
      "Same POS / KDS / kiosk experience as iPad",
      "Pairs with the same North terminal",
    ],
    bulletsEs: [
      "Samsung Galaxy Tab, Lenovo Tab, cualquier Android 10+",
      "Misma experiencia POS / KDS / kiosko que iPad",
      "Empareja con el mismo terminal North",
    ],
  },
  {
    icon: Smartphone,
    pillEn: "Coming soon",
    pillEs: "Muy pronto",
    pillColor: "text-white",
    pillBg: "bg-redy-charcoal",
    comingSoon: true,
    titleEn: "Tap to Pay on iPhone",
    titleEs: "Tap to Pay on iPhone",
    bodyEn:
      "Coming soon: the mobile app turns your iPhone into a POS that takes contactless payments with Tap to Pay on iPhone — no separate card terminal.",
    bodyEs:
      "Muy pronto: la app móvil convierte tu iPhone en un POS que acepta pagos contactless con Tap to Pay on iPhone — sin terminal de tarjetas aparte.",
    bulletsEn: [
      "Tap to Pay on iPhone — coming soon",
      "No separate card terminal — your iPhone takes the tap",
      "Ideal for catering, pop-ups, mobile vendors",
    ],
    bulletsEs: [
      "Tap to Pay on iPhone — muy pronto",
      "Sin terminal aparte — tu iPhone recibe el tap",
      "Ideal para catering, pop-ups, vendedores móviles",
    ],
  },
];

export default function HardwareDevices() {
  const { t } = useLang();

  return (
    <section className="py-24 px-6 bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto">
        <AnimatedSection className="text-center mb-16">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
            {t("Bring your own device", "Trae tu propio dispositivo")}
          </div>
          <h2 className="text-4xl md:text-[52px] font-black tracking-tight text-redy-charcoal leading-[1.05]">
            {t("Runs on what you already own.", "Corre en lo que ya tienes.")}
            <br />
            <span className="text-redy-charcoal/30">
              {t("Soon on what's in your pocket.", "Pronto en lo que llevas en el bolsillo.")}
            </span>
          </h2>
          <p className="text-[15px] font-medium text-redy-secondary max-w-2xl mx-auto mt-6">
            {t(
              "Redy is software-first. The North terminal below is optional — pair it for chip and tap, or skip it once the mobile app launches with Tap to Pay on iPhone (coming soon).",
              "Redy es software-first. El terminal North que ves abajo es opcional — empáralo para chip y tap, o sáltatelo cuando lance la app móvil con Tap to Pay on iPhone (muy pronto)."
            )}
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {DEVICES.map((device, i) => {
            const Icon = device.icon;
            const isComingSoon = !!device.comingSoon;
            return (
              <AnimatedElement
                key={device.titleEn}
                delay={0.08 * i}
                className={`relative rounded-[32px] p-8 flex flex-col gap-5 transition-all hover:-translate-y-1 ${
                  isComingSoon
                    ? "bg-redy-charcoal text-white shadow-2xl"
                    : "bg-white border border-black/5 shadow-sm hover:shadow-md"
                }`}
              >
                {/* Pill badge */}
                <div
                  className={`absolute top-5 right-5 inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black tracking-widest uppercase ${
                    isComingSoon
                      ? "bg-white/10 text-white border border-white/15"
                      : `${device.pillBg} ${device.pillColor}`
                  }`}
                >
                  {isComingSoon && <Sparkles className="w-3 h-3 fill-white" />}
                  {t(device.pillEn, device.pillEs)}
                </div>

                {/* Icon */}
                <div
                  className={`w-14 h-14 rounded-[18px] flex items-center justify-center ${
                    isComingSoon
                      ? "bg-white/10 border border-white/15"
                      : "bg-[#FAFAFA] border border-black/5"
                  }`}
                >
                  <Icon
                    className={`w-6 h-6 ${isComingSoon ? "text-redy-orange" : "text-redy-charcoal"}`}
                  />
                </div>

                {/* Title */}
                <h3
                  className={`text-[22px] font-black tracking-tight ${
                    isComingSoon ? "text-white" : "text-redy-charcoal"
                  }`}
                >
                  {t(device.titleEn, device.titleEs)}
                </h3>

                {/* Body */}
                <p
                  className={`text-[14px] font-medium leading-relaxed ${
                    isComingSoon ? "text-white/65" : "text-redy-secondary"
                  }`}
                >
                  {t(device.bodyEn, device.bodyEs)}
                </p>

                {/* Bullets */}
                <ul className="space-y-2 mt-auto pt-2">
                  {device.bulletsEn.map((b, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <CheckCircle2
                        className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${
                          isComingSoon ? "text-redy-orange" : "text-redy-green"
                        }`}
                      />
                      <span
                        className={`text-[12.5px] font-medium leading-snug ${
                          isComingSoon ? "text-white/80" : "text-redy-charcoal"
                        }`}
                      >
                        {t(b, device.bulletsEs[j])}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Coming-soon footer note */}
                {isComingSoon && (
                  <div className="mt-2 pt-4 border-t border-white/10 text-[11px] font-bold tracking-wide text-white/55">
                    {t(
                      "Beta access soon — sign up to get early access.",
                      "Beta pronto — registrate para acceso temprano."
                    )}
                  </div>
                )}
              </AnimatedElement>
            );
          })}
        </div>
      </div>
    </section>
  );
}
